import { useEffect, useRef } from "react";
import { authFetch } from "../../HttpClient";

// 2 min sans interaction clavier/souris = utilisateur considéré inactif :
// le temps ne doit pas s'accumuler si l'écran est juste resté ouvert sans
// être réellement consulté.
const IDLE_TIMEOUT_MS = 2 * 60 * 1000;
// Flush périodique vers le backend — borne aussi la perte de données en cas
// de fermeture brutale de l'onglet (au pire ce délai, voir plus bas).
const FLUSH_INTERVAL_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "scroll", "click", "touchstart"];

// Chronomètre le temps réellement actif (onglet visible + utilisateur non
// inactif) passé sur chaque écran (currentView de Home.js), et l'envoie par
// petits deltas à /api/users/session-activity, qui les cumule sur le
// LoginLog le plus récent de l'utilisateur (la "session" courante côté
// Super Admin).
//
// Pas de remise à zéro explicite nécessaire : l'accumulateur vit en mémoire
// (refs), donc il repart naturellement de zéro à chaque rechargement de
// page — exactement le moment où pingSessionIfDue() (App.js) peut créer un
// nouveau LoginLog si plus de 30 min se sont écoulées. Le flush périodique
// plafonne la perte de données au pire à FLUSH_INTERVAL_MS en cas de
// fermeture brutale de l'onglet (navigator.sendBeacon n'a pas été retenu :
// il ne peut pas porter le header d'authentification JWT).
export function useSectionActivityTracker(currentView) {
  const secondsByViewRef = useRef({});
  const lastActivityAtRef = useRef(Date.now());
  const isVisibleRef = useRef(document.visibilityState === "visible");
  const currentViewRef = useRef(currentView);

  const flushView = (view) => {
    if (!view) return;
    const seconds = secondsByViewRef.current[view] || 0;
    if (seconds <= 0) return;
    secondsByViewRef.current[view] = 0;

    authFetch("https://backend-myalfa.vercel.app/api/users/session-activity", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ view, seconds }),
    }).catch((err) =>
      console.error("Erreur lors du flush de l'activité de session :", err)
    );
  };

  // Changement d'écran : on flush l'ancien avant de continuer le chrono sur le nouveau.
  useEffect(() => {
    const previousView = currentViewRef.current;
    if (previousView && previousView !== currentView) {
      flushView(previousView);
    }
    currentViewRef.current = currentView;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);

  useEffect(() => {
    const markActive = () => {
      lastActivityAtRef.current = Date.now();
    };
    const handleVisibility = () => {
      isVisibleRef.current = document.visibilityState === "visible";
      if (isVisibleRef.current) markActive();
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, markActive, { passive: true })
    );
    document.addEventListener("visibilitychange", handleVisibility);

    const tickInterval = setInterval(() => {
      const isActive = Date.now() - lastActivityAtRef.current < IDLE_TIMEOUT_MS;
      const view = currentViewRef.current;
      if (isVisibleRef.current && isActive && view) {
        secondsByViewRef.current[view] = (secondsByViewRef.current[view] || 0) + 1;
      }
    }, 1000);

    const flushInterval = setInterval(() => {
      flushView(currentViewRef.current);
    }, FLUSH_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActive));
      document.removeEventListener("visibilitychange", handleVisibility);
      clearInterval(tickInterval);
      clearInterval(flushInterval);
      // Dernier flush au démontage (best effort — ne couvre pas une fermeture
      // brutale de l'onglet, seulement un démontage React normal).
      flushView(currentViewRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
